import React, { useState, useEffect } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import api from '../../services/api';

const UserPermissions = ({ user, onBack }) => {
    const [permissions, setPermissions] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);

    const modules = [
        'SHE Compliance', 
        'Risk Registers', 
        'Risk Assessment', 
        'Controls & Treatment', 
        'Loss Events', 
        'Monte Carlo Analysis', 
        'Business Continuity', 
        'Analysis & Reports', 
        'User Management'
    ];

    useEffect(() => {
        if (user) fetchPermissions(user.user_id);
    }, [user]);

    const fetchPermissions = async (userId) => {
        setLoading(true);
        try {
            const resp = await api.get(`/permissions/${userId}`);
            const data = resp.data;
            const mapped = {};
            data.forEach(p => {
                mapped[p.section_name] = { 
                    view: !!p.can_view, 
                    add: !!p.can_add, 
                    edit: !!p.can_edit, 
                    delete: !!p.can_delete 
                };
            });
            setPermissions(mapped || {});
        } catch (e) { 
            console.error(e);
            setMessage({ type: 'error', text: 'Failed to fetch user-specific access protocols.' });
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePerm = (module, type) => {
        const current = permissions[module] || { view: false, add: false, edit: false, delete: false };
        setPermissions({
            ...permissions,
            [module]: { ...current, [type]: !current[type] }
        });
    };

    const handleSavePermissions = async () => {
        setLoading(true);
        try {
            await api.post(`/permissions/${user.user_id}`, { permissions, entity_type: 'user' });
            setMessage({ type: 'success', text: `Access protocols successfully synchronized for ${user.first_name}.` });
        } catch (e) { 
            setMessage({ type: 'error', text: 'Protocol synchronization failed with server error.' });
        } finally {
            setLoading(false);
        }
    };

    if (!user) return <div className="card glass">No user context selected.</div>;

    return (
        <div className="animate-fade-in" style={{ paddingBottom: '60px' }}>
            <header style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <button 
                        onClick={onBack} 
                        className="btn btn-secondary" 
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '8px 16px' }}
                    >
                        <ArrowBackIcon style={{ fontSize: '18px' }} /> Back to Administration
                    </button>
                    <h2 className="gradient-text" style={{ fontSize: '28px', marginBottom: '8px' }}>Individual Access Override</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                        Customizing protocols for <strong>{user.first_name} {user.last_name}</strong> ({user.username})
                    </p>
                </div>
                <button 
                    className="btn btn-primary" 
                    onClick={handleSavePermissions} 
                    disabled={loading}
                    style={{ height: '48px', padding: '0 24px', display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                    <SaveIcon style={{ fontSize: '20px' }} />
                    {loading ? 'Synchronizing...' : 'Save User Access Protocols'}
                </button>
            </header>

            {message.text && (
                <div className={`card glass animate-slide-up`} style={{ 
                    marginBottom: '24px', 
                    padding: '16px 24px', 
                    borderLeft: `4px solid ${message.type === 'error' ? 'var(--accent)' : 'var(--primary)'}` 
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '20px' }}>{message.type === 'error' ? '⚠️' : '✅'}</span>
                        <span style={{ fontWeight: 600 }}>{message.text}</span>
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '20px' }}>
                {modules.map(module => (
                    <div key={module} className="card glass animate-fade-in" style={{ padding: '24px', border: '1px solid var(--border-color)' }}>
                        <div style={{ fontWeight: 700, marginBottom: '16px', color: 'var(--primary)', fontSize: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                            {module}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            {['view', 'add', 'edit', 'delete'].map(perm => (
                                <label key={perm} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px', 
                                    cursor: 'pointer', 
                                    fontSize: '14px',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    background: 'rgba(255,255,255,0.02)',
                                    transition: 'background 0.2s'
                                }}>
                                    <input 
                                        type="checkbox" 
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        checked={permissions[module]?.[perm] || false} 
                                        onChange={() => handleTogglePerm(module, perm)} 
                                    />
                                    <span style={{ textTransform: 'capitalize', color: permissions[module]?.[perm] ? 'white' : 'var(--text-muted)' }}>
                                        {perm}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            
            <div style={{ marginTop: '40px', textAlign: 'center', padding: '32px', background: 'rgba(255,255,255,0.01)', borderRadius: '16px', border: '1px dashed var(--border-color)' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '13px', maxWidth: '600px', margin: '0 auto' }}>
                    Note: Redundant Role permissions are automatically inherited. These overrides take precedence over department-wide or role-based configurations.
                </p>
            </div>
        </div>
    );
};

export default UserPermissions;
